/**
 * StatusBadge Component
 * 
 * Reusable status badge for displaying various status states
 */

import { FC } from 'react';
import { CircleCheckBig, Clock, CircleX, LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export type StatusType = 'success' | 'partial' | 'failed' | 'pending';

interface StatusConfig {
    className: string;
    icon: LucideIcon;
    label: string;
}

const statusConfigs: Record<StatusType, StatusConfig> = {
    success: {
        className: 'bg-emerald-900/50 text-emerald-400 border-emerald-700/50',
        icon: CircleCheckBig,
        label: 'Success',
    },
    partial: {
        className: 'bg-amber-900/50 text-amber-400 border-amber-700/50',
        icon: Clock,
        label: 'Partial',
    },
    failed: {
        className: 'bg-red-900/50 text-red-400 border-red-700/50',
        icon: CircleX,
        label: 'Failed',
    },
    pending: {
        className: 'bg-blue-900/50 text-blue-400 border-blue-700/50',
        icon: Clock,
        label: 'Pending',
    },
};

export interface StatusBadgeProps {
    status: StatusType;
    showLabel?: boolean;
    className?: string;
}

export const StatusBadge: FC<StatusBadgeProps> = ({ 
    status, 
    showLabel = true,
    className = '' 
}) => {
    const config = statusConfigs[status] || statusConfigs.success;
    const IconComponent = config.icon;

    return (
        <Badge 
            variant="outline" 
            className={cn(config.className, 'flex items-center gap-1', className)}
        >
            <IconComponent className="w-3 h-3" />
            {showLabel && <span>{config.label}</span>}
        </Badge>
    );
};
